import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import { SeoSchema, BreadcrumbSchema, getOrganizationSchema, getWebSiteSchema, getWebPageSchema, getFAQSchema } from '@/components/base/SeoSchema';

export default function AboutPage() {
  return (
    <>
      <SeoSchema type="Organization" data={getOrganizationSchema() as Record<string, unknown>} />
      <SeoSchema type="WebSite" data={getWebSiteSchema() as Record<string, unknown>} />
      <SeoSchema
        type="WebPage"
        data={getWebPageSchema(
          '关于我们 - 韩漫阅读 | 免费下拉式韩国漫画平台',
          '了解韩漫阅读的品牌故事、平台优势与联系方式。我们致力于提供免费无删减韩国漫画，打造最佳下拉式在线阅读体验。',
          'https://hanmanyuedu.com/about'
        ) as Record<string, unknown>}
      />
      <BreadcrumbSchema
        items={[
          { name: '首页', item: 'https://hanmanyuedu.com' },
          { name: '关于我们', item: 'https://hanmanyuedu.com/about' },
        ]}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="max-w-4xl mx-auto px-4 md:px-6 py-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '关于我们' },
              ]}
            />

            <article>
              <header className="mb-10 text-center">
                <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-3" style={{ fontFamily: 'var(--font-heading)' }}>
                  关于韩漫阅读
                </h1>
                <p className="text-sm text-foreground-500 max-w-2xl mx-auto">
                  韩漫阅读是一个专注于免费韩国漫画在线阅读的平台，致力于为漫画爱好者提供最优质的阅读体验。
                </p>
              </header>

              <section className="mb-12">
                <h2 className="text-lg font-bold text-foreground-950 mb-4 flex items-center gap-2" style={{ fontFamily: 'var(--font-heading)' }}>
                  <div className="w-1 h-5 bg-primary-500 rounded-full"></div>
                  <a href="#brand-story">品牌故事</a>
                </h2>
                <div className="bg-background-50 border border-background-200/70 rounded-lg p-6 space-y-4">
                  <p className="text-sm text-foreground-700 leading-relaxed">
                    韩漫阅读诞生于对韩国漫画的热爱。我们的创始团队是一群资深的韩国漫画爱好者，深知寻找高质量、无删减韩国漫画资源的困难。
                  </p>
                  <p className="text-sm text-foreground-700 leading-relaxed">
                    2024年，我们决定创建一个平台，让所有喜欢韩国漫画的读者都能轻松找到并阅读自己喜爱的作品。从最初收录几十部漫画的小站，发展到现在涵盖恋爱、奇幻、动作、悬疑、日常、剧情等多个分类的漫画大全平台，我们始终秉持"免费、完整、优质"的核心理念。
                  </p>
                  <p className="text-sm text-foreground-700 leading-relaxed">
                    我们采用先进的下拉式阅读技术，让读者可以像浏览社交媒体一样自然地阅读漫画，无需频繁翻页点击。同时，我们严格保证所有上架作品均为无删减完整版，尊重创作者的劳动成果，也尊重读者的阅读权利。
                  </p>
                </div>
              </section>

              <section className="mb-12">
                <h2 className="text-lg font-bold text-foreground-950 mb-4 flex items-center gap-2" style={{ fontFamily: 'var(--font-heading)' }}>
                  <div className="w-1 h-5 bg-accent-500 rounded-full"></div>
                  <a href="#advantages">平台优势</a>
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {[
                    { icon: 'ri-shield-check-line', title: '完全免费', desc: '所有漫画作品永久免费阅读，无隐藏收费，无需订阅会员。' },
                    { icon: 'ri-file-copy-line', title: '无删减完整版', desc: '每一部漫画都保持原作的完整性，绝不删减任何内容。' },
                    { icon: 'ri-smartphone-line', title: '下拉式阅读', desc: '专为移动端优化的下拉式浏览方式，流畅丝滑的阅读体验。' },
                    { icon: 'ri-refresh-line', title: '稳定更新', desc: '热门连载作品保持稳定的更新频率，不错过任何精彩内容。' },
                    { icon: 'ri-global-line', title: '多设备支持', desc: '完美适配电脑、平板和手机，随时随地开始阅读。' },
                    { icon: 'ri-shield-star-line', title: '品质保障', desc: '严格筛选漫画质量，只收录画风精美、剧情优秀的作品。' },
                  ].map((item, i) => (
                    <div key={i} className="flex gap-3 bg-background-50 border border-background-200/70 rounded-lg p-5">
                      <div className="w-10 h-10 flex items-center justify-center flex-shrink-0 rounded-full bg-primary-50">
                        <i className={`${item.icon} text-lg text-primary-500`}></i>
                      </div>
                      <div>
                        <h3 className="text-sm font-semibold text-foreground-950 mb-1">{item.title}</h3>
                        <p className="text-xs text-foreground-600 leading-relaxed">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              <section className="mb-12">
                <h2 className="text-lg font-bold text-foreground-950 mb-4 flex items-center gap-2" style={{ fontFamily: 'var(--font-heading)' }}>
                  <div className="w-1 h-5 bg-secondary-500 rounded-full"></div>
                  <a href="#reputation">平台信誉</a>
                </h2>
                <div className="bg-background-50 border border-background-200/70 rounded-lg p-6">
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 text-center mb-6">
                    {[
                      { value: '500万+', label: '月活跃用户' },
                      { value: '2000+', label: '收录漫画' },
                      { value: '99.7%', label: '用户好评率' },
                      { value: '3年', label: '持续运营' },
                    ].map((stat, i) => (
                      <div key={i}>
                        <div className="text-2xl font-bold text-primary-500 mb-1" style={{ fontFamily: 'var(--font-heading)' }}>{stat.value}</div>
                        <div className="text-xs text-foreground-500">{stat.label}</div>
                      </div>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    韩漫阅读自上线以来，始终坚持为用户提供优质的免费韩漫阅读服务。我们获得了广大漫画爱好者的认可与信赖，成为国内领先的韩国漫画在线阅读平台之一。我们严格遵守版权法规，所有漫画资源均通过合法渠道获取，并标注原作者信息，尊重创作者的权益。同时，我们建立了完善的用户反馈机制，及时处理读者的问题和建议，持续优化平台体验。
                  </p>
                </div>
              </section>

              <section className="mb-12">
                <h2 className="text-lg font-bold text-foreground-950 mb-4 flex items-center gap-2" style={{ fontFamily: 'var(--font-heading)' }}>
                  <div className="w-1 h-5 bg-primary-500 rounded-full"></div>
                  <a href="#contact">联系我们</a>
                </h2>
                <div className="bg-background-50 border border-background-200/70 rounded-lg p-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {[
                      { icon: 'ri-mail-line', label: '电子邮箱', value: 'contact@hanman.com' },
                      { icon: 'ri-customer-service-2-line', label: '在线客服', value: '工作日 9:00 - 18:00' },
                      { icon: 'ri-wechat-line', label: '微信公众号', value: '韩漫阅读官方' },
                      { icon: 'ri-map-pin-line', label: '办公地址', value: '上海市浦东新区张江高科技园区' },
                    ].map((item, i) => (
                      <div key={i} className="flex items-center gap-3">
                        <div className="w-10 h-10 flex items-center justify-center flex-shrink-0 rounded-full bg-background-100">
                          <i className={`${item.icon} text-lg text-foreground-700`}></i>
                        </div>
                        <div>
                          <p className="text-xs text-foreground-500">{item.label}</p>
                          <p className="text-sm text-foreground-900 font-medium">{item.value}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </section>

              <section className="mb-12">
                <SeoSchema type="FAQPage" data={getFAQSchema() as Record<string, unknown>} />
                <h2 className="text-lg font-bold text-foreground-950 mb-4 flex items-center gap-2" style={{ fontFamily: 'var(--font-heading)' }}>
                  <div className="w-1 h-5 bg-accent-500 rounded-full"></div>
                  <a href="#about-faq">常见问题</a>
                </h2>
                <div className="space-y-3">
                  {[
                    { q: '韩漫网站上的漫画都是免费的吗？', a: '是的，韩漫阅读网站上的所有韩国漫画作品均完全免费阅读，无需付费订阅或购买。我们致力于为广大漫画爱好者提供无删减、完整版的高质量韩漫资源。' },
                    { q: '什么是下拉式漫画阅读方式？', a: '下拉式阅读是一种专为手机和网页优化的漫画浏览方式，您只需向下滑动页面即可连续阅读漫画内容，无需翻页点击，带来流畅丝滑的阅读体验。这也是韩国网络漫画（Webtoon）最主流的呈现形式。' },
                    { q: '网站上的漫画是否有删减？', a: '本站所有漫画均为无删减完整版，保持原作的完整内容和画风，让您体验到原汁原味的韩国漫画作品。我们尊重创作者的劳动成果，维护作品的完整性。' },
                    { q: '韩漫网站支持哪些设备阅读？', a: '韩漫阅读网站采用响应式设计，完美支持电脑、平板和手机等多种设备。无论您使用什么设备，都能获得舒适流畅的下拉式阅读体验。推荐使用手机竖屏阅读，效果最佳。' },
                    { q: '漫画更新频率是怎样的？', a: '本站热门连载漫画通常保持每周更新一话的节奏，部分作品可能会根据作者进度有所调整。您可以收藏喜欢的作品，方便随时追踪最新章节的更新动态。' },
                  ].map((faq, i) => (
                    <div key={i} className="bg-background-50 border border-background-200/70 rounded-lg p-4">
                      <h3 className="text-sm font-semibold text-foreground-950 mb-2">{faq.q}</h3>
                      <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                    </div>
                  ))}
                </div>
              </section>
            </article>
          </div>
        </main>
        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
}